export class WebGL2UnsupportedError extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = 'WebGL2UnsupportedError';
  }
}

export interface GLCapabilities {
  maxTextureSize: number;
  maxRenderbufferSize: number;
  vendor: string;
  renderer: string;
}

export interface GLContext {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  caps: GLCapabilities;
}

/**
 * The canvas only redraws when something invalidates it, so the drawing buffer
 * has to survive between browser composites: without that, any repaint the page
 * makes on its own — hovering a panel is enough — presents an uninitialised
 * buffer and the image flashes. `desynchronized` is off for the same reason; its
 * low-latency present path tears and flickers on Windows.
 */
const CONTEXT_ATTRIBUTES: WebGLContextAttributes = {
  alpha: false,
  depth: false,
  stencil: false,
  antialias: false,
  premultipliedAlpha: false,
  preserveDrawingBuffer: true,
  powerPreference: 'high-performance',
};

export function isWebGL2Available(): boolean {
  try {
    const probe = document.createElement('canvas');
    return Boolean(probe.getContext('webgl2'));
  } catch {
    return false;
  }
}

export function createGLContext(canvas: HTMLCanvasElement): GLContext {
  const gl = canvas.getContext('webgl2', CONTEXT_ATTRIBUTES);
  if (!gl) {
    throw new WebGL2UnsupportedError(
      'The browser did not provide a WebGL2 context. Update your browser or enable hardware acceleration.',
    );
  }

  // R8 render targets (selection and layer masks) are core in WebGL2, but the
  // float blit path used by the feather blur needs this extension on some drivers.
  gl.getExtension('EXT_color_buffer_float');

  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  const caps: GLCapabilities = {
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number,
    vendor: debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string)
      : (gl.getParameter(gl.VENDOR) as string),
    renderer: debugInfo
      ? (gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
      : (gl.getParameter(gl.RENDERER) as string),
  };

  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);

  return { gl, canvas, caps };
}
